import { useState, useEffect, useRef } from 'react';
import { getReadRelays } from './useNostrPublish';

const BCH_ADDRESS_D_TAG = 'bchnostr/bch-address';

// Cache BCH addresses in memory — stores { address, fetchedAt }
// so we can distinguish "not fetched" from "fetched but no address".
interface CacheEntry {
  address: string | null;
  fetchedAt: number;
}
const addressCache: Map<string, CacheEntry> = new Map();

// How long a "null" result stays cached before allowing re-fetch (5 minutes).
// A found address stays cached indefinitely within the session.
const NULL_CACHE_TTL = 5 * 60 * 1000;

/** Clear cached address so it gets re-fetched */
export function invalidateBCHAddressCache(pubkey: string) {
  addressCache.delete(pubkey);
}

/** Read cached BCH address synchronously (null if not yet fetched or no address). */
export function getCachedBCHAddress(pubkey: string): string | null {
  return addressCache.get(pubkey)?.address ?? null;
}

/** Whether a given pubkey's address has been fetched already and is still valid. */
export function hasBCHAddressCached(pubkey: string): boolean {
  const entry = addressCache.get(pubkey);
  if (!entry) return false;
  // If the cached result is a found address, it's always valid
  if (entry.address) return true;
  // If it's null (not found), check TTL so we can retry later
  return (Date.now() - entry.fetchedAt) < NULL_CACHE_TTL;
}

// In-flight prefetch promises to avoid duplicate fetches
const _prefetchInFlight: Map<string, Promise<string | null>> = new Map();

/** Prefetch a BCH address into cache (no React hook needed). Returns a promise.
 *
 *  Lookup strategy (most reliable → fallback):
 *    1. Kind 30078 with d-tag "bchnostr/bch-address" — the dedicated address event
 *    2. Kind 0 profile metadata — check for `bch_address` field in the JSON content
 *
 *  Both kinds are queried IN PARALLEL across all read relays PLUS the BCH Nostr
 *  relay (which is authoritative for BCHNostr users). If kind 30078 returns a
 *  result we use that immediately. Otherwise we fall back to kind 0.
 *
 *  This fixes the common case where a user saved their BCH address but the
 *  kind 30078 publish silently failed or only reached a subset of relays.
 */
export function prefetchBCHAddress(pubkey: string): Promise<string | null> {
  // Return from cache if still valid
  if (hasBCHAddressCached(pubkey)) return Promise.resolve(getCachedBCHAddress(pubkey));
  if (_prefetchInFlight.has(pubkey)) return _prefetchInFlight.get(pubkey)!;

  const promise = new Promise<string | null>((resolve) => {
    // Query ALL read relays + always include relay.bchnostr.com (authoritative)
    const baseRelays = getReadRelays();
    const BCH_RELAY = 'wss://relay.bchnostr.com';
    const relays = baseRelays.includes(BCH_RELAY) ? baseRelays : [BCH_RELAY, ...baseRelays];

    let bestKind30078: any = null;   // Dedicated BCH address event
    let bestKind0Addr: string | null = null; // Fallback from profile metadata
    let bestKind0CreatedAt = 0;
    let responded = 0;
    let errored = 0;              // relays that failed/timed out (inconclusive)
    let finalized = false;
    const totalExpected = relays.length;
    const websockets: WebSocket[] = [];

    // Resolve the best address we have from whatever events arrived.
    const resolveAddr = (): string | null => {
      // Prefer kind 30078 (dedicated), fall back to kind 0 profile bch_address.
      if (bestKind30078?.content?.trim()) {
        const a = normalizeBCHAddress(bestKind30078.content.trim());
        if (a) return a;
      }
      if (bestKind0Addr) {
        const a = normalizeBCHAddress(bestKind0Addr);
        if (a) return a;
      }
      return null;
    };

    // `inconclusive` = we found nothing AND at least one relay never gave us a
    // clean EOSE (error/timeout). In that case we must NOT cache the null, or a
    // transient relay hiccup poisons the cache for 5 minutes and every later
    // tip to this user wrongly shows "No BCH address". We still resolve null so
    // the current call doesn't hang, but the NEXT lookup retries from scratch.
    const finalize = (inconclusive = false) => {
      if (finalized) return;
      finalized = true;

      const addr = resolveAddr();
      // Cache a positive result always. Cache a null only when the lookup was
      // conclusive (every relay cleanly EOSE'd and genuinely had no address).
      if (addr || !inconclusive) {
        addressCache.set(pubkey, { address: addr, fetchedAt: Date.now() });
      }
      _prefetchInFlight.delete(pubkey);
      websockets.forEach(ws => { try { ws.close(); } catch {} });
      resolve(addr);
    };

    const maybeFinalize = () => {
      // Short-circuit ONLY when the dedicated kind-30078 event carries a VALID
      // address — otherwise keep waiting for the kind-0 fallback to arrive.
      if (!finalized && bestKind30078?.content?.trim() && normalizeBCHAddress(bestKind30078.content.trim())) {
        finalize();
        return;
      }
      // Once every relay has either responded (clean EOSE) or errored, finalize.
      // Mark the result inconclusive if ANY relay errored and we found nothing,
      // so a flaky relay never gets cached as a hard "no address".
      if (responded + errored >= totalExpected) {
        finalize(errored > 0);
      }
    };

    relays.forEach(url => {
      try {
        const ws = new WebSocket(url);
        websockets.push(ws);
        const subA = 'bchpre_a_' + Math.random().toString(36).slice(2, 8);
        const subB = 'bchpre_b_' + Math.random().toString(36).slice(2, 8);
        let gotEoseA = false;
        let gotEoseB = false;

        ws.onopen = () => {
          // Query 1: Kind 30078 dedicated BCH address event
          ws.send(JSON.stringify(['REQ', subA, {
            kinds: [30078],
            authors: [pubkey],
            '#d': [BCH_ADDRESS_D_TAG],
            limit: 1,
          }]));
          // Query 2: Kind 0 profile (fallback for bch_address field)
          ws.send(JSON.stringify(['REQ', subB, {
            kinds: [0],
            authors: [pubkey],
            limit: 1,
          }]));
        };
        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT') {
              const ev = data[2];
              if (data[1] === subA && ev) {
                // Kind 30078 — keep newest
                if (!bestKind30078 || ev.created_at > bestKind30078.created_at) {
                  bestKind30078 = ev;
                }
              } else if (data[1] === subB && ev?.kind === 0) {
                // Kind 0 — extract bch_address from profile JSON
                if (ev.created_at > bestKind0CreatedAt) {
                  try {
                    const profile = JSON.parse(ev.content);
                    if (profile.bch_address && typeof profile.bch_address === 'string') {
                      bestKind0Addr = profile.bch_address.trim();
                      bestKind0CreatedAt = ev.created_at;
                    }
                  } catch {}
                }
              }
            }
            if (data[0] === 'EOSE') {
              if (data[1] === subA) gotEoseA = true;
              if (data[1] === subB) gotEoseB = true;
              // Only count this relay as responded when both subs have EOSE'd
              if (gotEoseA && gotEoseB) {
                responded++;
                maybeFinalize();
              }
            }
          } catch {}
        };
        ws.onerror = () => { errored++; maybeFinalize(); };
        setTimeout(() => { try { ws.close(); } catch {} }, 8000);
      } catch {
        errored++;
        maybeFinalize();
      }
    });

    // Safety timeout: if some relays never answered, treat as inconclusive so
    // we don't poison the cache with a false "no address".
    setTimeout(() => finalize(responded + errored < totalExpected), 9000);
  });

  _prefetchInFlight.set(pubkey, promise);
  return promise;
}

/**
 * Look up a user's BCH address from their kind 30078 event
 * with d-tag "bchnostr/bch-address".
 * This is how BCHNostr stores BCH receiving addresses — separate from profile metadata.
 */
export function useBCHAddress(pubkey: string | undefined): { address: string | null; loading: boolean } {
  const [address, setAddress] = useState<string | null>(
    pubkey ? getCachedBCHAddress(pubkey) : null
  );
  const [loading, setLoading] = useState(pubkey ? !hasBCHAddressCached(pubkey) : false);
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pubkey) { setLoading(false); return; }

    // Check cache (with TTL for null results)
    if (hasBCHAddressCached(pubkey)) {
      setAddress(getCachedBCHAddress(pubkey));
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches for the same pubkey
    if (fetchedRef.current === pubkey) return;
    fetchedRef.current = pubkey;
    setLoading(true);

    // Use the prefetch function which queries all relays
    prefetchBCHAddress(pubkey).then(addr => {
      setAddress(addr);
      setLoading(false);
    });
  }, [pubkey]);

  return { address, loading };
}

/**
 * Extract a CashAddr payload from user input.
 *
 * Handles many edge cases that occur in the wild:
 *   - Full CashAddr: bitcoincash:qz...
 *   - Bare payload:  qz...
 *   - URI with params: bitcoincash:qz...?amount=1
 *   - eCash prefix: ecash:qz...  (same format, wrong chain)
 *   - Extra whitespace/newlines
 *   - Uppercase CashAddr: BITCOINCASH:QZ... or QZ...
 *   - JSON-quoted: "qz..."
 *
 * Returns the bare lowercase payload (without prefix), or null if not CashAddr.
 */
function extractCashAddrPayload(addr: string): string | null {
  if (!addr || typeof addr !== 'string') return null;

  // Clean up: trim, remove quotes, take first line only
  let clean = addr.trim().replace(/^["']|["']$/g, '').split(/[\n\r]/)[0].trim();

  // Strip known CashAddr-family prefixes (bitcoincash:, bchtest:, ecash:)
  clean = clean.replace(/^(bitcoincash|bchtest|ecash):/i, '');

  // Strip URI params: ?amount=1&label=...
  clean = clean.split('?')[0].trim();

  // Lowercase for validation
  clean = clean.toLowerCase();

  // Validate: CashAddr payload, exactly 42 chars from the CashAddr base32
  // alphabet. The leading char encodes the address version:
  //   q = P2PKH            p = P2SH
  //   z = P2PKH token-aware r = P2SH token-aware (CashTokens)
  // Token-aware addresses (z/r) are the SAME key/script as their q/p form —
  // they only signal that the holder can receive CashTokens — so they are
  // fully valid BCH recipients and must be accepted here.
  const CASHADDR_RE = /^[qpzr][qpzry9x8gf2tvdw0s3jn54khce6mua7l]{41}$/;
  if (CASHADDR_RE.test(clean)) {
    return clean;
  }

  return null;
}

/**
 * Validate a BCH address.
 *
 * Only accepts CashAddr format — legacy base58 addresses (1... or 3...)
 * are deliberately REJECTED because prepending "bitcoincash:" to a
 * legacy address creates an invalid CashAddr that mainnet-js will refuse.
 * Users with legacy addresses need to convert them to CashAddr format first.
 */
export function isValidBCHAddress(addr: string): boolean {
  return extractCashAddrPayload(addr) !== null;
}

/**
 * Normalize a BCH address to full CashAddr format: `bitcoincash:<payload>`.
 *
 * Always returns lowercase. Handles bare payloads, prefixed addresses,
 * mixed case, URIs with params, and eCash-prefixed addresses.
 *
 * Returns null if the address is not a valid CashAddr.
 */
export function normalizeBCHAddress(addr: string): string | null {
  const payload = extractCashAddrPayload(addr);
  if (!payload) return null;
  return 'bitcoincash:' + payload;
}

